/* eslint-disable no-use-before-define */
import React, {
  createContext, useRef, useEffect, Children, forwardRef
} from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import TabLabel from '@/components/TabLabel';
import TabContent from '@/components/TabContent';
import Cell from '@/components/Cell';
import CellItem from '@/components/CellItem';
import ScrollableSpace from '@/components/ScrollableSpace';
import useStyle from './style';
import cx from '@/utils/cx';

const TabsContext = createContext(null);

const Tabs = forwardRef(({
  validation,
  formatter,
  children,
  value,
  onChange,
  _color,
  swipe,
  scrollableLabels,
  labelProps,
  className,
  ...rest
} , ref) => {
  const innerContainerRef = useRef();

  const labelsChildren = Children.toArray(children).filter((child) => child.type === TabLabel);
  const contentsChildren = Children.toArray(children).filter((child) => child.type === TabContent);

  const theme = useTheme();
  const classes = useStyle({ contentsLength: contentsChildren.length });

  const nextValue = (v, offset) => {
    const currentIndex = contentsChildren.findIndex((child) => child.props.id === v);
    const index = (() => {
      const calced = currentIndex + offset;
      return calced > 0
        ? calced % contentsChildren.length
        : (calced + contentsChildren.length) % contentsChildren.length;
    })();
    return contentsChildren[index].props.id;
  };
  const innerContainerTransform = (v, offsetPx = 0) => {
    const currentIndex = contentsChildren.findIndex((child) => child.props.id === v);
    const factor = 100 / contentsChildren.length;
    return `translateX(calc(${currentIndex * factor * (theme.direction.start === 'right' ? 1 : -1)}% + ${offsetPx}px))`;
  };

  useEffect(() => {
    innerContainerRef.current.style.transform = innerContainerTransform(value);
  }, [value]);

  let startX = 0;
  const bindEvents = (parts = ['inner', 'window']) => {
    if (!swipe) {
      return;
    }
    if (parts.includes('inner')) {
      startX = 0;
      innerContainerRef.current.addEventListener('mousedown', onTouchStart);
      innerContainerRef.current.addEventListener('touchstart', onTouchStart);
    }
    if (parts.includes('window')) {
      window.addEventListener('mousemove', onTouchMove);
      window.addEventListener('touchmove', onTouchMove);
      window.addEventListener('mouseup', onTouchEnd);
      window.addEventListener('touchend', onTouchEnd);
    }
  };
  const unbindEvents = (parts = ['inner', 'window']) => {
    if (parts.includes('inner') && innerContainerRef.current) {
      innerContainerRef.current.removeEventListener('mousedown', onTouchStart);
      innerContainerRef.current.removeEventListener('touchstart', onTouchStart);
    }
    if (parts.includes('window')) {
      window.removeEventListener('mousemove', onTouchMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', onTouchEnd);
      window.removeEventListener('touchend', onTouchEnd);
    }
  };

  const getTouchX = (e) => (
    e.changedTouches && e.changedTouches.length ? e.changedTouches[0].clientX : e.pageX
  );

  const onTouchStart = (e) => {
    bindEvents(['window']);
    startX = getTouchX(e);
    innerContainerRef.current.style.transitionDuration = '0s';
  };
  const onTouchMove = (e) => {
    const x = getTouchX(e);
    const changedX = (x - startX);
    if (changedX) {
      e.stopPropagation();
      e.preventDefault();
    }
    innerContainerRef.current.style.transform = innerContainerTransform(value, changedX);
  };
  const onTouchEnd = (e) => {
    innerContainerRef.current.style.transitionDuration = null;
    const x = getTouchX(e);
    const changedX = (x - startX) * (theme.direction.start === 'right' ? 1 : -1);
    // eslint-disable-next-line no-nested-ternary
    const nextValueOffset = changedX <= -70 ? -1 : (changedX > 70 ? 1 : 0);
    const nextTab = nextValue(value, nextValueOffset);
    if (nextTab !== value) {
      onChange(nextTab);
    } else {
      innerContainerRef.current.style.transform = innerContainerTransform(value, 0);
    }
    unbindEvents(['window']);
  };

  useEffect(() => {
    innerContainerRef.current.style.transform = innerContainerTransform(value);
    bindEvents(['inner']);
    return () => {
      unbindEvents(['inner', 'window']);
    };
  });

  const { Provider } = TabsContext;

  const Labels = (
    <Cell className={classes.tabsLabelContainer} fill _padding="no" autoWrap={false} {...labelProps}>
      { labelsChildren.map((child) => (
        <CellItem align="center" grow key={`tab-label-${child.props.contentId}`}>
          { child }
        </CellItem>
      )) }
    </Cell>
  );

  const Contents = (
    <div className={cx(classes.tabsContentContainer, className)} ref={ref}>
      <div className="inner" ref={innerContainerRef}>
        { contentsChildren }
      </div>
    </div>
  );

  return (
    <Provider value={{ _color, onChange, value }}>
      <div {...rest}>
        {
          scrollableLabels
            ? <ScrollableSpace>{Labels}</ScrollableSpace>
            : Labels
        }
        { Contents }
      </div>
    </Provider>
  );
});

Tabs.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  _color: PropTypes.string,
  swipe: PropTypes.bool,
  scrollableLabels: PropTypes.bool,
  labelProps: PropTypes.bool,
  className: PropTypes.string,
};

Tabs.defaultProps = {
  _color: 'primary',
  swipe: true,
  scrollableLabels: false,
  labelProps: false,
  className: ''
};

export { Tabs, TabsContext };
export default Tabs;
